import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { ECheqFilterProps, getECheqRequestFilter } from "./filter-obj";
import { toaster } from "@/components/ui/toaster";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { AppRoutes } from "@/constants/routes";
import { NavigateFunction } from "react-router-dom";
import {
  getECheqRequest,
  getECheqRequests,
  GetECheqRequestsProps,
} from "@/services/echeq-requests.service";
import {
  ECheqRequestDetail,
  ECheqRequestListResponse,
} from "./echeq-requests.schema";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("fecha_desde", undefined);
  setValue("fecha_hasta", undefined);
  setValue("sucursal", []);
  setValue("request_amount", "");
  setValue("requester", "");
  setValue("account_number", "");
  setValue("estados", []);

  submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchECheqRequests: (filters?: any) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchECheqRequests,
  setOpenFilters,
  navigate,
  fetchPermissions,
}: SubmitFiltersHandlerProps) => {
  try {
    setFiltersApplied(true);

    const filterObj: Partial<GetECheqRequestsProps> = getECheqRequestFilter(
      event as ECheqFilterProps,
    );

    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

    await fetchECheqRequests(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ECHEQ_REQUESTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorObj?.message ?? "No se pudieron aplicar los filtros.",
      });
    }
    throw error;
  }
};

interface FetchECheqRequestsHandlerProps {
  showLoad?: boolean;
  setLoadingECheqRequests: Dispatch<SetStateAction<boolean>>;
  setECheqRequests: Dispatch<SetStateAction<ECheqRequestListResponse>>;
  filters?: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchECheqRequestsHandler = async ({
  showLoad = true,
  setLoadingECheqRequests,
  setECheqRequests,
  filters,
  navigate,
  fetchPermissions,
}: FetchECheqRequestsHandlerProps) => {
  try {
    if (showLoad) setLoadingECheqRequests(true);

    const response = await getECheqRequests({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setECheqRequests(
      (response ?? {
        listado: [],
        totalPages: 1,
        totalRecords: 0,
        pendingRequests: false,
      }) as ECheqRequestListResponse,
    );

    setLoadingECheqRequests(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo cargar la lista de solicitudes de E-Cheq.",
      });
    }
    throw error;
  } finally {
    setLoadingECheqRequests(false);
  }
};

interface FetchECheqRequestDetailHandlerProps {
  id?: string;
  showLoad?: boolean;
  setLoadingRequest: Dispatch<SetStateAction<boolean>>;
  setRequest: Dispatch<SetStateAction<ECheqRequestDetail | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchECheqRequestDetailHandler = async ({
  id,
  showLoad = true,
  setLoadingRequest,
  setRequest,
  navigate,
  fetchPermissions,
}: FetchECheqRequestDetailHandlerProps) => {
  try {
    if (showLoad) setLoadingRequest(true);

    const response = await getECheqRequest({ Id: Number(id) });

    setRequest(response);
    setLoadingRequest(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo cargar el detalle de la solicitud de E-Cheq.",
      });
    }
    throw error;
  }
};
